/**
 * The v1 story operations (spec 023) and the 021 scene operations wrapped to
 * name their scene. Each is pure: it never mutates `doc`, and takes every new
 * id from `ctx.newId()` in order (listed in `createdIds`). `apply` assumes
 * args that passed `inputSchema` (`runStoryOperation` checks it).
 */
import type { ChoiceOption, StoryGraphEdge } from '../@types';
import { createDefaultSceneContent } from '../utils/sceneContentDefaults';
import { normaliseSceneName, SCENE_NAME_MAX_LENGTH } from '../utils/sceneName';
import { runOperation } from '../operations';
import type { JsonSchema, OperationContext, SceneDocument, SceneOperation } from '../operations/types';
import {
  connectRule, dropNodes, edgeConditionRule, nextNodePosition, positionAfter,
  FIRST_NODE_POSITION, NODE_ROW_HEIGHT, NODE_SPACING_X,
  type ConditionRefusal, type ConnectRefusal,
} from './graphRules';
import { sceneByKey } from './document';
import { hasBackgroundStates } from '../utils/backgroundStates';
import { moveBackgroundSlotToState, pictureSlug } from '../pictures/pictureMap';
import type {
  StoryDocNode, StoryDocScene, StoryDocument, StoryOperation, StoryOperationError, StoryOperationErrorCode,
  StoryOperationResult,
} from './types';

// ─── Helpers ────────────────────────────────────────────────────────────────

const str = (description: string): JsonSchema => ({ type: 'string', description });
const obj = (properties: Record<string, JsonSchema>, required: string[] = []): JsonSchema => ({
  type: 'object', properties, required, additionalProperties: false,
});

const SCENE_KEY_SCHEMA = str('The scene key from the story summary: ss-<n> for a scene of the story, or the new-<id> key createScene returned.');
const NODE_SCHEMA = (what: string) => str(`${what}: a node id from the story summary or one an operation returned.`);

const e = (code: StoryOperationErrorCode, message: string, path?: string): StoryOperationError =>
  (path ? { code, message, path } : { code, message });
const fail = (...errors: StoryOperationError[]): StoryOperationResult => ({ ok: false, errors });

/** `prefix` + the id, unless the id source already returned it in that form. */
const withPrefix = (prefix: string, id: string) => (id.startsWith(prefix) ? id : `${prefix}${id}`);

const nodeById = (doc: StoryDocument, id: string) => doc.nodes.find((n) => n.id === id);
const choicesOfNode = (doc: StoryDocument) => (n: StoryDocNode): ChoiceOption[] =>
  sceneByKey(doc, n.sceneKey)?.doc.choices ?? [];

/** `node-3 ("Intro")`, or the bare id for a node whose scene is unknown. */
function nodeText(doc: StoryDocument, id: string): string {
  const n = nodeById(doc, id);
  const s = n && sceneByKey(doc, n.sceneKey);
  return s ? `${id} ("${s.doc.name || s.name}")` : id;
}

const CONNECT_CODES: Readonly<Record<ConnectRefusal, StoryOperationErrorCode>> = {
  'missing-node': 'not_found',
  'needs-choice': 'needs_choice',
  'not-a-choice': 'not_a_choice',
  'choice-taken': 'choice_taken',
  'no-choices': 'no_choices',
  'next-exists': 'next_exists',
};

const CONDITION_CODES: Readonly<Record<ConditionRefusal, StoryOperationErrorCode>> = {
  'missing-edge': 'not_found',
  'not-a-choice': 'not_a_choice',
  'choice-taken': 'choice_taken',
  'needs-choice': 'needs_choice',
  'next-exists': 'next_exists',
};

// ─── 1. createScene ─────────────────────────────────────────────────────────

export interface CreateSceneArgs { name: string; after?: string }

export const createScene: StoryOperation<CreateSceneArgs> = {
  name: 'createScene',
  description: 'Create a new Storyboard scene (empty; it plays as long as its content) owned by this story and place its node on the map. Returns the new scene key and node id; the first node of an empty map becomes the start.',
  inputSchema: obj({
    name: str(`The scene name (1–${SCENE_NAME_MAX_LENGTH} characters).`),
    after: NODE_SCHEMA('Place the node one column to the right of this node'),
  }, ['name']),
  apply(doc, args, ctx) {
    const name = normaliseSceneName(args.name);
    const errors: StoryOperationError[] = [];
    if (!name) errors.push(e('out_of_range', 'name must not be empty', 'name'));
    const after = args.after !== undefined ? nodeById(doc, args.after) : undefined;
    if (args.after !== undefined && !after) errors.push(e('not_found', `no node ${args.after}`, 'after'));
    if (errors.length) return fail(...errors);

    const key = withPrefix('new-', ctx.newId('scene'));
    const nodeId = withPrefix('node-', ctx.newId('node'));
    const sceneDoc = { ...createDefaultSceneContent(), name } as unknown as SceneDocument;
    const scene: StoryDocScene = { key, mode: 'new', name, doc: sceneDoc };
    const node: StoryDocNode = {
      id: nodeId,
      sceneKey: key,
      position: after ? positionAfter(doc.nodes, after) : nextNodePosition(doc.nodes),
    };
    const becomesStart = doc.nodes.length === 0 || !doc.nodes.some((n) => n.id === doc.startNodeId);
    return {
      ok: true,
      doc: {
        ...doc,
        nodes: [...doc.nodes, node],
        scenes: [...doc.scenes, scene],
        startNodeId: becomesStart ? nodeId : doc.startNodeId,
      },
      summary: `Created scene "${name}" (${key}) as node ${nodeId}${after ? ` right of ${after.id}` : ''}${becomesStart ? ', the start' : ''}`,
      createdIds: [key, nodeId],
    };
  },
};

// ─── 2. addNode ─────────────────────────────────────────────────────────────

export interface AddNodeArgs { scene: string }

export const addNode: StoryOperation<AddNodeArgs> = {
  name: 'addNode',
  description: 'Put a node on the map for a story scene that has none. A scene already on the map returns its node and changes nothing.',
  inputSchema: obj({ scene: SCENE_KEY_SCHEMA }, ['scene']),
  apply(doc, args, ctx) {
    const scene = sceneByKey(doc, args.scene);
    if (!scene) return fail(e('not_found', `no scene ${args.scene} in this story`, 'scene'));
    const existing = doc.nodes.find((n) => n.sceneKey === scene.key);
    if (existing) {
      return { ok: true, doc, summary: `Scene "${scene.doc.name || scene.name}" is already on the map as ${existing.id}`, createdIds: [] };
    }
    const nodeId = withPrefix('node-', ctx.newId('node'));
    const node: StoryDocNode = { id: nodeId, sceneKey: scene.key, position: nextNodePosition(doc.nodes) };
    const becomesStart = doc.nodes.length === 0 || !doc.nodes.some((n) => n.id === doc.startNodeId);
    return {
      ok: true,
      doc: { ...doc, nodes: [...doc.nodes, node], startNodeId: becomesStart ? nodeId : doc.startNodeId },
      summary: `Placed scene "${scene.doc.name || scene.name}" as node ${nodeId}${becomesStart ? ', the start' : ''}`,
      createdIds: [nodeId],
    };
  },
};

// ─── 3. removeNode ──────────────────────────────────────────────────────────

export interface RemoveNodeArgs { nodeId: string }

export const removeNode: StoryOperation<RemoveNodeArgs> = {
  name: 'removeNode',
  description: 'Remove a node and every connection touching it from the map. Its scene stays in the story\'s scene list (a scene created in this batch is not created at all).',
  inputSchema: obj({ nodeId: NODE_SCHEMA('The node to remove') }, ['nodeId']),
  apply(doc, args) {
    const node = nodeById(doc, args.nodeId);
    if (!node) return fail(e('not_found', `no node ${args.nodeId}`, 'nodeId'));
    const label = nodeText(doc, node.id);
    const graph = { nodes: doc.nodes, edges: doc.edges, startNodeId: doc.startNodeId };
    dropNodes(graph, new Set([node.id]));
    const nodes = graph.nodes as StoryDocNode[];
    const scene = sceneByKey(doc, node.sceneKey);
    // D-4: a scene created in this batch whose last node goes is not created.
    const dropScene = !!scene && scene.mode === 'new' && !nodes.some((n) => n.sceneKey === scene.key);
    const parts = [`Removed node ${label}`];
    const edgesGone = doc.edges.length - graph.edges.length;
    if (edgesGone) parts.push(`and ${edgesGone} connection${edgesGone === 1 ? '' : 's'}`);
    let summary = parts.join(' ');
    if (doc.startNodeId === node.id) summary += graph.startNodeId ? `; the start is now ${graph.startNodeId}` : '; the map has no start';
    if (dropScene) summary += `; scene "${scene!.name}" (${scene!.key}) is not created`;
    else if (scene && !nodes.some((n) => n.sceneKey === scene.key)) summary += `; scene ${scene.key} stays in the story's scene list`;
    return {
      ok: true,
      doc: {
        ...doc,
        nodes,
        edges: graph.edges,
        startNodeId: graph.startNodeId,
        scenes: dropScene ? doc.scenes.filter((s) => s !== scene) : doc.scenes,
      },
      summary,
      createdIds: [],
    };
  },
};

// ─── 4. setStartNode ────────────────────────────────────────────────────────

export interface SetStartNodeArgs { nodeId: string }

export const setStartNode: StoryOperation<SetStartNodeArgs> = {
  name: 'setStartNode',
  description: 'Make this node the start of the story.',
  inputSchema: obj({ nodeId: NODE_SCHEMA('The new start node') }, ['nodeId']),
  apply(doc, args) {
    if (!nodeById(doc, args.nodeId)) return fail(e('not_found', `no node ${args.nodeId}`, 'nodeId'));
    if (doc.startNodeId === args.nodeId) {
      return { ok: true, doc, summary: `${nodeText(doc, args.nodeId)} is already the start`, createdIds: [] };
    }
    return { ok: true, doc: { ...doc, startNodeId: args.nodeId }, summary: `The story now starts at ${nodeText(doc, args.nodeId)}`, createdIds: [] };
  },
};

// ─── 5. connect ─────────────────────────────────────────────────────────────

export interface ConnectArgs { from: string; to: string; choiceId?: string }

export const connect: StoryOperation<ConnectArgs> = {
  name: 'connect',
  description: 'Connect two nodes. From a scene with choices, give the choiceId this connection follows (each choice gets exactly one); from a scene without choices, leave it out (at most one next scene).',
  inputSchema: obj({
    from: NODE_SCHEMA('The source node'),
    to: NODE_SCHEMA('The target node'),
    choiceId: str('The source scene\'s choice this connection follows (required when it has choices).'),
  }, ['from', 'to']),
  apply(doc, args, ctx) {
    const missing: StoryOperationError[] = [];
    if (!nodeById(doc, args.from)) missing.push(e('not_found', `no node ${args.from}`, 'from'));
    if (!nodeById(doc, args.to)) missing.push(e('not_found', `no node ${args.to}`, 'to'));
    if (missing.length) return fail(...missing);
    const rule = connectRule(doc, choicesOfNode(doc), args.from, args.to, args.choiceId);
    if (rule.ok === false) {
      const path = rule.reason === 'not-a-choice' || rule.reason === 'choice-taken' || rule.reason === 'no-choices' || rule.reason === 'needs-choice'
        ? 'choiceId' : undefined;
      return fail(e(CONNECT_CODES[rule.reason], rule.message, path));
    }
    const edge: StoryGraphEdge = { id: withPrefix('edge-', ctx.newId('edge')), from: args.from, to: args.to };
    if (rule.condition) {
      edge.condition = rule.condition;
      edge.label = rule.label ?? '';
    }
    return {
      ok: true,
      doc: { ...doc, edges: [...doc.edges, edge] },
      summary: `Connected ${nodeText(doc, args.from)} → ${nodeText(doc, args.to)}${edge.condition ? ` on "${edge.label}"` : ''} (${edge.id})`,
      createdIds: [edge.id],
    };
  },
};

// ─── 6. disconnect ──────────────────────────────────────────────────────────

export interface DisconnectArgs { edgeId: string }

export const disconnect: StoryOperation<DisconnectArgs> = {
  name: 'disconnect',
  description: 'Remove one connection.',
  inputSchema: obj({ edgeId: str('The connection id from the story summary.') }, ['edgeId']),
  apply(doc, args) {
    const edge = doc.edges.find((x) => x.id === args.edgeId);
    if (!edge) return fail(e('not_found', `no connection ${args.edgeId}`, 'edgeId'));
    return {
      ok: true,
      doc: { ...doc, edges: doc.edges.filter((x) => x !== edge) },
      summary: `Removed the connection ${nodeText(doc, edge.from)} → ${nodeText(doc, edge.to)} (${edge.id})`,
      createdIds: [],
    };
  },
};

// ─── 7. setEdgeCondition ────────────────────────────────────────────────────

export interface SetEdgeConditionArgs { edgeId: string; choiceId: string }

export const setEdgeCondition: StoryOperation<SetEdgeConditionArgs> = {
  name: 'setEdgeCondition',
  description: 'Set which choice of the source scene a connection follows, or "none" to make it the unconditional next scene (only from a scene without choices).',
  inputSchema: obj({
    edgeId: str('The connection id.'),
    choiceId: str('A choice id of the source scene, or "none".'),
  }, ['edgeId', 'choiceId']),
  apply(doc, args) {
    const condition = args.choiceId === 'none' ? null : args.choiceId;
    const rule = edgeConditionRule(doc, choicesOfNode(doc), args.edgeId, condition);
    if (rule.ok === false) {
      return fail(e(CONDITION_CODES[rule.reason], rule.message, rule.reason === 'missing-edge' ? 'edgeId' : 'choiceId'));
    }
    const edges = doc.edges.map((x) => {
      if (x.id !== args.edgeId) return x;
      const { condition: _c, label: _l, ...rest } = x;
      return rule.condition ? { ...rest, condition: rule.condition, label: rule.label ?? '' } : rest;
    });
    return {
      ok: true,
      doc: { ...doc, edges },
      summary: rule.condition
        ? `Connection ${args.edgeId} now follows "${rule.label}" (${rule.condition})`
        : `Connection ${args.edgeId} is now the unconditional next scene`,
      createdIds: [],
    };
  },
};

// ─── 8. arrangeGraph ────────────────────────────────────────────────────────

export type ArrangeGraphArgs = Record<string, never>;

/** Positions by breadth-first depth from the start; unreachable nodes in a row below. */
export function arrangedPositions(doc: Pick<StoryDocument, 'nodes' | 'edges' | 'startNodeId'>): Map<string, { x: number; y: number }> {
  const ids = new Set(doc.nodes.map((n) => n.id));
  const depth = new Map<string, number>();
  const columns: string[][] = [];
  if (ids.has(doc.startNodeId)) {
    depth.set(doc.startNodeId, 0);
    columns.push([doc.startNodeId]);
    const queue = [doc.startNodeId];
    while (queue.length) {
      const id = queue.shift()!;
      const d = depth.get(id)!;
      for (const edge of doc.edges) {
        if (edge.from !== id || !ids.has(edge.to) || depth.has(edge.to)) continue;
        depth.set(edge.to, d + 1);
        (columns[d + 1] ??= []).push(edge.to);
        queue.push(edge.to);
      }
    }
  }
  const out = new Map<string, { x: number; y: number }>();
  let tallest = 0;
  columns.forEach((col, d) => {
    tallest = Math.max(tallest, col.length);
    col.forEach((id, i) => out.set(id, { x: FIRST_NODE_POSITION.x + d * NODE_SPACING_X, y: FIRST_NODE_POSITION.y + i * NODE_ROW_HEIGHT }));
  });
  const rowY = FIRST_NODE_POSITION.y + (tallest + (tallest > 0 ? 1 : 0)) * NODE_ROW_HEIGHT;
  doc.nodes.filter((n) => !depth.has(n.id)).forEach((n, i) => {
    out.set(n.id, { x: FIRST_NODE_POSITION.x + i * NODE_SPACING_X, y: rowY });
  });
  return out;
}

export const arrangeGraph: StoryOperation<ArrangeGraphArgs> = {
  name: 'arrangeGraph',
  description: 'Tidy the map: lay the nodes out left to right by their distance from the start, with unreachable nodes in a row below. Positions only.',
  inputSchema: obj({}),
  apply(doc) {
    const positions = arrangedPositions(doc);
    let moved = 0;
    const nodes = doc.nodes.map((n) => {
      const p = positions.get(n.id)!;
      if (p.x === n.position.x && p.y === n.position.y) return n;
      moved += 1;
      return { ...n, position: p };
    });
    if (!moved) return { ok: true, doc, summary: 'The map is already arranged', createdIds: [] };
    return { ok: true, doc: { ...doc, nodes }, summary: `Arranged the map (${moved} node${moved === 1 ? '' : 's'} moved)`, createdIds: [] };
  },
};

// ─── 9. renameStory ─────────────────────────────────────────────────────────

export interface RenameStoryArgs { name: string }

export const STORY_NAME_MAX_LENGTH = 255;

export const renameStory: StoryOperation<RenameStoryArgs> = {
  name: 'renameStory',
  description: `Rename the story (1–${STORY_NAME_MAX_LENGTH} characters after trimming).`,
  inputSchema: obj({ name: str(`The new story name (1–${STORY_NAME_MAX_LENGTH} characters).`) }, ['name']),
  apply(doc, args) {
    const name = args.name.trim();
    if (!name || name.length > STORY_NAME_MAX_LENGTH) {
      return fail(e('out_of_range', `name must be 1–${STORY_NAME_MAX_LENGTH} characters`, 'name'));
    }
    if (name === doc.name) return { ok: true, doc, summary: `Story name unchanged (${name})`, createdIds: [] };
    return { ok: true, doc: { ...doc, name }, summary: `Renamed the story to ${name}`, createdIds: [] };
  },
};

// ─── Wrapped 021 scene operations ───────────────────────────────────────────

/** 021's operation `op`, run on the scene its `scene` argument names. */
export function wrapSceneOperation(op: SceneOperation<unknown>): StoryOperation<{ scene: string } & Record<string, unknown>> {
  const base = op.inputSchema;
  return {
    name: op.name,
    description: `${op.description} Works on the story scene named by its key (scene).`,
    inputSchema: {
      ...base,
      properties: { scene: SCENE_KEY_SCHEMA, ...(base.properties ?? {}) },
      required: ['scene', ...(base.required ?? [])],
    },
    apply(doc, args, ctx: OperationContext) {
      const { scene: key, ...rest } = args;
      const scene = sceneByKey(doc, key);
      if (!scene) return fail(e('not_found', `no scene ${key} in this story`, 'scene'));
      const result = runOperation(op.name, scene.doc, rest, ctx);
      if (result.ok === false) return { ok: false, errors: result.errors };

      const sceneDoc = result.doc;
      const nextScene: StoryDocScene = { ...scene, doc: sceneDoc };
      if (op.name === 'renameScene') nextScene.name = sceneDoc.name;
      let edges = doc.edges;
      let dangling = '';
      if (op.name === 'setChoices') {
        // Labels mirror the choices (010's syncChoiceEdges); an edge whose
        // choice went stays, and dangles, as after a hand edit.
        const nodeIds = new Set(doc.nodes.filter((n) => n.sceneKey === key).map((n) => n.id));
        const choices = sceneDoc.choices ?? [];
        const gone: StoryGraphEdge[] = [];
        edges = doc.edges.map((edge) => {
          if (!nodeIds.has(edge.from) || !edge.condition) return edge;
          const match = choices.find((c) => c.id === edge.condition);
          if (!match) { gone.push(edge); return edge; }
          return edge.label === match.text ? edge : { ...edge, label: match.text };
        });
        if (gone.length) {
          dangling = `; ${gone.map((g) => `the connection ${g.id} (${nodeText(doc, g.from)} → ${nodeText(doc, g.to)}${g.label ? ` on "${g.label}"` : ''})`).join(', ')} now dangle${gone.length === 1 ? 's' : ''}: its choice was removed`;
        }
      }
      const shared = scene.mode === 'reference' ? ' (this scene is shared with other stories, so it changes there too)' : '';
      const scenes = doc.scenes.map((s) => (s === scene ? nextScene : s));
      let pictureMap = doc.pictureMap;
      // Spec 039 D-2: a background's first states take its stored slot, unless another
      // scene still shows a background of that name with no states.
      if (op.name === 'setBackgroundStates' && !hasBackgroundStates(scene.doc.background) && hasBackgroundStates(sceneDoc.background)) {
        const slug = pictureSlug(sceneDoc.background.name);
        const stateless = scenes.some((s) => pictureSlug(s.doc.background?.name) === slug && !hasBackgroundStates(s.doc.background));
        if (!stateless) {
          const slots = moveBackgroundSlotToState(doc.pictureMap.slots, sceneDoc.background.name ?? '', sceneDoc.background.defaultState!);
          if (slots !== doc.pictureMap.slots) pictureMap = { ...doc.pictureMap, slots };
        }
      }
      return {
        ok: true,
        doc: { ...doc, edges, scenes, pictureMap },
        summary: `Scene "${scene.doc.name || scene.name}" (${key}): ${result.summary}${dangling}${shared}`,
        createdIds: result.createdIds,
      };
    },
  };
}
